import { PriceCalculation } from '@idearoom/types/lib';
import { ClientDataFixedColumns } from '../constants/ClientDataFixedColumns';
import { ClientDataType } from '../constants/ClientDataType';
import {
  DisplayColumns,
  RegionPricingColumns,
  PricingCalculationColumns,
  COMPONENT_CATEGORY_MAPPING,
} from '../constants/ComponentPricing';
import { PricingComponentEditFields } from '../constants/FormFields';
import { ComponentCategory, ComponentCategoryItem } from '../types/ComponentPricing';
import { TableData } from '../types/DataGrid';
import { addDispatchCommandToUndo } from './undoManagerUtils';
import { updatePricingComponentRows } from '../ducks/pricingSlice';
import { arePriceValuesDifferent, getValidatedNewValue } from './pricingUtils';
import { UpdateClientDataRow } from '../ducks/clientDataSlice';
import { FormData } from '../components/PricingComponentEditForm';
import { mapClientAndDataTypeAndTableToUndoStackId } from './clientIdUtils';
import { AppDispatch } from '../hooks';
import { fuzzyMatchIncludes } from './stringUtils';
import { I18nKeys } from '../constants/I18nKeys';
import { i18n } from '../i18n';

/**
 * Gets the starting value for a component pricing field.
 *
 * @param column column name
 * @param rowData data for the component row
 * @returns initial value for the component pricing field
 */
export const getDefaultParsedComponentPricingColumnValue = (
  column: keyof ComponentCategoryItem,
  rowData?: ComponentCategoryItem,
) => {
  const { t } = i18n;

  const value = rowData?.[column];
  if (Object.values(PricingCalculationColumns).some((v) => v === column)) {
    return (
      (rowData?.priceExpression && t(I18nKeys.PricingComponentPriceExpressionCalculation)) ||
      value ||
      PriceCalculation.Amount
    );
  }
  if (Object.values(RegionPricingColumns).some((v) => v === column) && typeof value === 'string') {
    const valueWithoutCurrency = value.replace(/[$€£¥₣,]/g, '');
    return (
      (rowData?.priceExpression && t(I18nKeys.PricingComponentPriceExpressionPrice)) ||
      parseFloat(valueWithoutCurrency)?.toFixed(2) ||
      null
    );
  }
  return value;
};

/**
 * Finds all available component pricing form fields for the given component ID's table columns
 * and includes initial values if data exists.
 * Pricing calculation fields default to 'Amount' if no value exists.
 *
 * @param componentRowId component row ID
 * @param componentCategoryItems all component data for the category
 * @returns component pricing form fields and initial values
 */
export const getComponentPricingFormInitialValues = (
  componentRowId: string,
  componentCategoryItems: ComponentCategoryItem[],
): Record<string, string | null | undefined> | undefined => {
  const componentData = componentCategoryItems.find((item) => item.rowId === componentRowId);

  if (!componentData) {
    return undefined;
  }

  const columns = Object.keys(componentData).filter((c) =>
    [DisplayColumns, [RegionPricingColumns.Price], PricingCalculationColumns].some((e) =>
      Object.values(e).some((v) => v === c),
    ),
  );
  return Object.fromEntries([
    [PricingComponentEditFields.Component, componentRowId],
    ...columns
      .map((c) => [c, getDefaultParsedComponentPricingColumnValue(c as keyof ComponentCategoryItem, componentData)])
      // Do not display price expression column if a value does not exist
      .filter(([c, v]) => !([DisplayColumns.PriceExpression] as string[]).includes(c as string) || !!v),
  ]);
};

export const getChangedValues = (updates: { data: TableData; column: string; oldValue: any; newValue: any }[]) => {
  const newValues: { data: TableData; column: string; oldValue: any; newValue: any }[] = [];

  updates.forEach(({ data, column, oldValue, newValue }) => {
    let [newVal, oldVal] = [newValue, oldValue].map((val) => `${val}`.trim());

    let valueChanged = oldVal !== newVal;
    if (Object.values(RegionPricingColumns).some((v) => v === column)) {
      [newVal, oldVal] = [newVal, oldVal].map((val) => getValidatedNewValue(val, oldVal));
      valueChanged = arePriceValuesDifferent(oldVal, newVal);
    } else if (Object.values(PricingCalculationColumns).some((v) => v === column)) {
      valueChanged = valueChanged && (!!oldValue || newVal !== PriceCalculation.Amount);
    }

    if (valueChanged) {
      newValues.push({ data: { ...data, [column]: newVal }, column, oldValue, newValue });
    }
  });

  return newValues;
};

const updateValues = (
  clientDataTableId: string,
  updates: { data: TableData; column: string; oldValue: any; newValue: any }[],
  dispatch: AppDispatch,
) => {
  const [, , table] = clientDataTableId.split(':');

  const newValues = getChangedValues(updates);

  const newRows: (UpdateClientDataRow & { table: string })[] = [];
  const oldRows: (UpdateClientDataRow & { table: string })[] = [];

  newValues.forEach(({ data, column, oldValue, newValue }) => {
    oldRows.push({ table, rowData: data, column, value: oldValue, formula: undefined });
    newRows.push({ table, rowData: data, column, value: newValue, formula: undefined });
  });

  if (newRows.length > 0) {
    addDispatchCommandToUndo(
      dispatch,
      [updatePricingComponentRows(oldRows)],
      [updatePricingComponentRows(newRows)],
      clientDataTableId,
      true,
    );
  }
};

export const onComponentEditSubmit = (
  formData: FormData,
  clientId: string,
  componentCategoryItems: ComponentCategoryItem[],
  dispatch: AppDispatch,
) => {
  const componentId = formData[PricingComponentEditFields.Component];
  const componentData = componentCategoryItems.find(
    ({ [ClientDataFixedColumns.RowId]: rowId }) => rowId === componentId,
  );

  if (!componentData) return;

  const clientDataTableId = mapClientAndDataTypeAndTableToUndoStackId(
    clientId,
    ClientDataType.Supplier,
    componentData.table,
  );

  const relevantComponentTableData = { [ClientDataFixedColumns.RowId]: componentData.rowId };

  const updates = Object.entries(formData)
    .filter(
      ([column]) =>
        [RegionPricingColumns, [DisplayColumns.Label]].flatMap((e) => Object.values(e)).includes(column) &&
        // Ignore changes to price and price calculation if price expression exists
        (!([RegionPricingColumns.Price, PricingCalculationColumns.PriceCalculation] as string[]).includes(column) ||
          !formData[DisplayColumns.PriceExpression]),
    )
    .map(([column, newValue]) => {
      const oldValue = componentData[column as keyof ComponentCategoryItem];
      return { data: relevantComponentTableData, column, oldValue, newValue };
    });

  if (!updates.length) return;

  updateValues(clientDataTableId, updates, dispatch);
};

export const filterComponentCategoryItems = (searchValue: string, componentCategoryItems?: ComponentCategoryItem[]) =>
  (componentCategoryItems || []).filter(
    (item) =>
      !searchValue ||
      Object.entries(item)
        .filter(([column]) =>
          (
            [PricingCalculationColumns, DisplayColumns, [RegionPricingColumns.Price]].flatMap((e) =>
              Object.values(e),
            ) as string[]
          ).includes(column),
        )
        .some(([_, val]) => fuzzyMatchIncludes(`${val}`, searchValue)),
  );

export const filterComponentCategories = (searchValue: string, componentCategories?: ComponentCategory[]) =>
  (componentCategories || []).filter(
    ({ key, count }) =>
      count > 0 &&
      (!searchValue ||
        [COMPONENT_CATEGORY_MAPPING[key]?.label, key].some((val) => fuzzyMatchIncludes(`${val}`, searchValue))),
  );
